import get from 'lodash/get'
import {createSelector} from 'reselect'

import isAfterDate from 'date-fns/is_after'
import isBeforeDate from 'date-fns/is_before'
import parseDate from 'date-fns/parse'

import {ApiRequestStates} from '../const'

import * as userSelectors from './user'

export const allUserOrders = (state) => get(state, 'orders.allUserOrders')
export const userOrdersRequestState = (state) => get(state, 'orders.userOrdersRequestState')

/**
 * For correctly routing a user that goes directly to the /start/replacements route (without entering)
 * from their events page), we attempt to find the earliest upcoming event with a shipped rental
 * order for the user. This logic to identify which orders are eligible for replacements is similar
 * to the logic of the cross.canRequestReplacementOrderItem selector. If the user has more than one
 * upcoming event with an eligible order for replacements, we return the order belonging to the event
 * that is coming up first.
 */
export const getFirstUpcomingRentalOrder = createSelector(
  allUserOrders,
  userSelectors.id,
  ($orders, $currentUserId) => {
    if (!$orders || $orders.length === 0) {
      return undefined
    }

    const hasBuyOrder = $orders.some((o) => !o.rental)
    if (hasBuyOrder) {
      return undefined
    }

    const earliestEligibleRentalOrder = () => {
      let earliestRentalOrder

      $orders.forEach((order) => {
        const isAnEligibleOrder = (o) => {
          return (
            !o.cancelled && !o.homeTryOn && o.rental && o.isShipped && o.userId === $currentUserId
          )
        }

        if (!earliestRentalOrder && isAnEligibleOrder(order)) {
          earliestRentalOrder = order
        }

        const eventDate = parseDate(order.eventDate)
        const earliestRentalEventDate = parseDate(earliestRentalOrder.eventDate)
        const today = new Date()

        if (
          isAnEligibleOrder(order) &&
          isAfterDate(eventDate, today) &&
          isBeforeDate(eventDate, earliestRentalEventDate)
        ) {
          earliestRentalOrder = order
        }
      })

      return earliestRentalOrder
    }

    return earliestEligibleRentalOrder()
  },
)

export const getUniqueUpcomingRentalOrder = createSelector(
  allUserOrders,
  userSelectors.id,
  ($orders, $currentUserId) => {
    if (!$orders || $orders.length === 0) return undefined

    const upcomingRentalOrders = $orders.filter((order) => {
      const eventDate = parseDate(order.eventDate)
      const today = new Date()
      return (
        isAfterDate(eventDate, today) &&
        !order.cancelled &&
        !order.homeTryOn &&
        order.rental &&
        order.isShipped &&
        order.userId === $currentUserId
      )
    })

    if (upcomingRentalOrders.length > 1) return undefined
    return upcomingRentalOrders[0]
  },
)

export const isUserOrdersRequestFailed = createSelector(
  userOrdersRequestState,
  ($requestState) => $requestState === ApiRequestStates.ERROR,
)

export const isUserOrdersRequestIdle = createSelector(
  userOrdersRequestState,
  ($requestState) => $requestState === ApiRequestStates.IDLE,
)

export const isUserOrdersRequestSuccess = createSelector(
  userOrdersRequestState,
  ($requestState) => $requestState === ApiRequestStates.SUCCESS,
)
